import React, { useEffect } from "react";
import "./AlertModal.css";

export type AlertType = "info" | "success" | "warning" | "error";

interface AlertModalProps {
  isOpen: boolean;
  title?: string;
  message: string;
  type?: AlertType;
  onClose: () => void;
  buttonText?: string;
  autoClose?: boolean;
  autoCloseDuration?: number;
}

const AlertModal: React.FC<AlertModalProps> = ({
  isOpen,
  title,
  message,
  type = "info",
  onClose,
  buttonText = "Tamam",
  autoClose = true,
  autoCloseDuration = 5000,
}) => {
  useEffect(() => {
    if (isOpen && autoClose) {
      const timer = setTimeout(() => {
        onClose();
      }, autoCloseDuration);

      return () => clearTimeout(timer);
    }
  }, [isOpen, autoClose, autoCloseDuration, onClose]);

  if (!isOpen) return null;

  const getIcon = () => {
    switch (type) {
      case "success":
        return (
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
        );
      case "warning":
        return (
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        );
      case "error":
        return (
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
        );
      default:
        return (
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
        );
    }
  };

  const getTitle = () => {
    if (title) return title;
    switch (type) {
      case "success":
        return "Başarılı";
      case "warning":
        return "Uyarı";
      case "error":
        return "Hata";
      default:
        return "Bilgi";
    }
  };

  return (
    <div className="alert-toast-container">
      <div className={`alert-toast alert-toast-${type}`}>
        <div className={`alert-toast-icon alert-icon-${type}`}>{getIcon()}</div>
        <div className="alert-toast-content">
          <h4 className="alert-toast-title">{getTitle()}</h4>
          <p className="alert-toast-message">{message}</p>
        </div>
        <button
          className="alert-toast-close"
          onClick={onClose}
          aria-label="Kapat"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        {autoClose && (
          <span
            className="alert-toast-progress"
            style={{ animationDuration: `${autoCloseDuration}ms` }}
          />
        )}
      </div>
    </div>
  );
};

export default AlertModal;
